import eyeOutlineIcon from '@/assets/icons/eye-outline-icon.png';
import pencilIcon from '@/assets/icons/pencil-icon.png';
import { IconWrapper } from '@/components/icon/IconWrapper.tsx';
import { MoreVert, PushPin } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { getSubprojectStatusIcon } from '../../statusUtils';
import { Subproject } from './types';
import { useOrg } from '@/organization/useOrg';
import { useGetSubproject } from '@/queries/useSubprojects';
import { useState, useEffect } from 'react';

import {
  getSequentialBarStatuses,
  getSubprojectProgress,
  getSubprojectStatusStyle,
} from './utils';

interface SubprojectItemProps {
  subproject: Subproject;
  projectId: string;
  isPinned: boolean;
  effectiveStatus: string;
  mainStatus: string;
  onTogglePin: (subprojectId: string) => void;
  onMenuClick: (e: React.MouseEvent<HTMLElement>, subprojectId: string) => void;
}

const BUILDING_IMAGE_OVERRIDES: Record<string, string> = {
  corrections: 'waste',
  'police/fire': 'policefire',
};

const buildingImageSrc = (name: string) => {
  const key = name.toLowerCase();
  const file = BUILDING_IMAGE_OVERRIDES[key] ?? key;
  return `/img/buildings/${file}.png`;
};

export function SubprojectItem({
  subproject,
  projectId,
  isPinned,
  effectiveStatus,
  mainStatus,
  onTogglePin,
  onMenuClick,
}: SubprojectItemProps) {
  const statusStyle = getSubprojectStatusStyle(effectiveStatus);
  const isReadOnly = mainStatus === 'Pencils Down' || mainStatus === 'Archived';
  const progress = getSubprojectProgress(subproject);
  const barStatuses = getSequentialBarStatuses(subproject);
  const { selectedOrg } = useOrg();
  const [canDecarbFlow, setCanDecarbFlow] = useState<boolean>(false);

  const { data: fetchedSubproject } = useGetSubproject(
    selectedOrg?.id || '',
    subproject.id || '',
  );

  useEffect(() => {
    if (fetchedSubproject) {
      // todo in future: once budget officer role exists, require program manager and budget officer here
      if (
        fetchedSubproject.name !== '' &&
        fetchedSubproject.id !== '' &&
        !!fetchedSubproject.projectShape
      ) {
        setCanDecarbFlow(true);
      }
    }
  }, [fetchedSubproject]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 'clamp(170px, 12vw, 240px)',
        '@media (min-width: 1025px)': {
          width: 'clamp(200px, 14vw, 280px)',
        },
        '@media (min-width: 1700px)': {
          width: 'clamp(230px, 16vw, 310px)',
        },
        borderRadius: 'clamp(4px, 0.42vw, 8px)',
        overflow: 'hidden',
        cursor: 'default',
        border: isReadOnly
          ? '2px solid rgba(9, 126, 113, 1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Top Section */}
      <Box
        sx={{
          backgroundColor: 'rgba(11, 54, 49, 1)',
          padding: 'clamp(6px, 0.4vh, 8px) clamp(6px, 0.5vw, 8px)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'clamp(4px, 0.3vw, 5px)',
          minHeight: 'clamp(32px, 2.5vh, 40px)',
        }}
      >
        {/* Icon */}
        {subproject.buildingUsages?.length > 0 ? (
          <Box
            component='img'
            src={buildingImageSrc(subproject.buildingUsages[0])}
            alt='Subproject'
            sx={{
              width: 'clamp(60px, 5.5vw, 66px)',
              height: 'auto',
              flexShrink: 0,
              marginTop: 'clamp(0px, 0.05vh, 1px)',
              overflow: 'visible',
              // the policefire image has less white space than the other images for some reason
              mx: subproject.buildingUsages[0] === 'Police/Fire' ? -2 : -3,
            }}
          />
        ) : (
          <IconWrapper icon={'Floors'} sx={{ height: 40, width: 40 }} />
        )}

        {/* Title and Status */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(12px, 0.9vw, 14px)',
              color: '#FFFFFF',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {subproject.name}
          </Typography>

          {/* Status Tag */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '2px',
              marginBottom: '4px',
            }}
          >
            <Box
              sx={{
                backgroundColor: statusStyle.bg,
                borderRadius: 'clamp(2px, 0.15vw, 3px)',
                px: 'clamp(3px, 0.3vw, 5px)',
                py: 'clamp(1px, 0.08vw, 2px)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(7px, 0.55vw, 9px)',
                  color: statusStyle.color,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }}
              >
                {statusStyle.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Pin and More buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 'clamp(2px, 0.2vw, 3px)',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(subproject.id);
            }}
            sx={{
              p: 'clamp(2px, 0.2vw, 3px)',
              color: isPinned ? '#FFFFFF' : 'rgba(35, 138, 125, 1)',
              '&:hover': {
                color: isPinned
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(45, 158, 145, 1)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <PushPin sx={{ fontSize: 'clamp(14px, 1vw, 16px)' }} />
          </IconButton>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, subproject.id);
            }}
            sx={{
              p: 'clamp(2px, 0.2vw, 3px)',
              color: 'rgba(35, 138, 125, 1)',
              '&:hover': {
                color: 'rgba(45, 158, 145, 1)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <MoreVert sx={{ fontSize: 'clamp(14px, 1vw, 16px)' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Bottom Section */}
      <Box
        sx={{
          backgroundColor: 'rgba(27, 84, 77, 1)',
          borderTop: '1.5px solid rgba(9, 126, 113, 1)',
          padding:
            'clamp(4px, 0.3vh, 6px) clamp(6px, 0.5vw, 8px) clamp(1.5px, 0.15vh, 2px)',
          display: 'flex',
          flexDirection: 'row',
          gap: 'clamp(6px, 0.5vw, 8px)',
          minHeight: 'clamp(20px, 1.5vh, 24px)',
        }}
      >
        {/* Left Column - Checklist and Progress Icons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(5px, 0.4vw, 7px)',
            flex: 1,
          }}
        >
          {/* Checklist Icon and Text */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 'clamp(3px, 0.25vw, 5px)',
              paddingLeft: 'clamp(3px, 0.25vw, 4px)',
            }}
          >
            {/* Custom checklist icon */}
            <Box
              component='svg'
              viewBox='0 0 24 24'
              sx={{
                width: 'clamp(11px, 0.85vw, 14px)',
                height: 'clamp(11px, 0.85vw, 14px)',
                fill: 'none',
              }}
            >
              <polyline
                points='2,6 4.5,8.5 8,4.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <line
                x1='11'
                y1='6.5'
                x2='19'
                y2='6.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <polyline
                points='2,12 4.5,14.5 8,10.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <line
                x1='11'
                y1='12.5'
                x2='19'
                y2='12.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <polyline
                points='2,18 4.5,20.5 8,16.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <line
                x1='11'
                y1='18.5'
                x2='19'
                y2='18.5'
                stroke='#FFFFFF'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(6px, 0.45vw, 8px)',
                color: '#FFFFFF',
              }}
            >
              {progress.label}
            </Typography>
          </Box>

          {/* Progress Icons */}
          <Box
            sx={{
              display: 'flex',
              gap: 'clamp(4px, 0.35vw, 7px)',
              alignItems: 'center',
              paddingLeft: 'clamp(3px, 0.25vw, 5px)',
              marginBottom: 'clamp(5px, 0.4vh, 7px)',
            }}
          >
            {/* Stage 1 */}
            <Box
              component='img'
              src={getSubprojectStatusIcon(barStatuses.bar1)}
              alt='Stage 1'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(7px, 0.6vw, 11px)',
                display: 'block',
                imageRendering: 'crisp-edges',
                flexShrink: 0,
              }}
            />
            {/* Stage 2 */}
            <Box
              component='img'
              src={getSubprojectStatusIcon(barStatuses.bar2)}
              alt='Stage 2'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(7px, 0.6vw, 11px)',
                display: 'block',
                imageRendering: 'crisp-edges',
                flexShrink: 0,
              }}
            />
            {/* Stage 3 */}
            <Box
              component='img'
              src={getSubprojectStatusIcon(barStatuses.bar3)}
              alt='Stage 3'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(7px, 0.6vw, 11px)',
                display: 'block',
                imageRendering: 'crisp-edges',
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>

        {/* Right Column - Edit/View button */}
        <Box
          component={Link}
          to={
            canDecarbFlow
              ? `/project/${projectId}/decarb/${subproject.id}`
              : `/project/${projectId}/proj-details`
          }
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          sx={{
            width: 'clamp(33px, 2.6vw, 42px)',
            height: 'clamp(19px, 1.48vw, 24px)',
            backgroundColor: 'rgba(11, 54, 49, 1)',
            borderRadius: 'clamp(4px, 0.35vw, 6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            alignSelf: 'center',
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'rgba(11, 54, 49, 0.8)',
            },
          }}
        >
          {isReadOnly ? (
            <Box
              component='img'
              src={eyeOutlineIcon}
              alt='View'
              sx={{
                width: 'clamp(16px, 1.2vw, 20px)',
                height: 'clamp(12px, 0.95vw, 15px)',
                display: 'block',
                filter: 'brightness(0) invert(1)',
              }}
            />
          ) : (
            <Box
              component='img'
              src={pencilIcon}
              alt='Edit'
              sx={{
                width: 'clamp(13px, 1vw, 16px)',
                height: 'clamp(13px, 1vw, 16px)',
                display: 'block',
                filter: 'brightness(0) invert(1)',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
